import React from 'react';
import PropTypes from 'prop-types';
import {Tab as ReactTab} from 'react-tabs';
import styled, {css} from 'styled-components';
import {
  copyFontSize,
  copyFontSizeSmall,
  colorBrandBase,
  colorBrandDark,
  colorWhite,
  copyFontFamily,
  copyColor,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset
} from '@nib-components/theme';
import breakpoint from 'styled-components-breakpoint';
import {px, p, ml} from 'styled-components-spacing';
//
import {STACKED_BREAKPOINT, BORDER_WIDTH, BLACK_20, WHITE_20} from './config';

export interface TabProps {
  selected?: boolean;
  subtitle?: string;
  variation?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
}

export interface TabWrapperProps {
  selected?: boolean;
  invert?: boolean;
  fill?: boolean;
  [key: string]: unknown;
}

export interface TitleProps {
  selected?: boolean;
  subtitle?: string;
  variation?: string;
  [key: string]: unknown;
}

const baseStyles = css<TabWrapperProps>`
  color: ${({invert}) => (invert ? colorWhite : copyColor)};
  font-family: ${copyFontFamily};
  border-bottom: 1px solid ${({invert}) => (invert ? WHITE_20 : BLACK_20)};
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    box-shadow: inset ${BORDER_WIDTH} 0px ${({invert}) => (invert ? WHITE_20 : BLACK_20)};
  }

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: -${focusOutlineOffset};
  }

  ${breakpoint(STACKED_BREAKPOINT)`
      display: inline-block;
      flex-shrink: 0;
      border-bottom: none;
      border-radius: 8px 8px 0 0;

      ${({fill}: {fill?: boolean}) =>
        fill &&
        `
        flex-grow: 1;
      `};
  `};

  ${({selected}) =>
    selected &&
    css`
      box-shadow: inset ${BORDER_WIDTH} 0px ${({invert}: {invert?: boolean}) => (invert ? colorWhite : colorBrandBase)};

      &:hover {
        box-shadow: inset ${BORDER_WIDTH} 0px ${({invert}: {invert?: boolean}) => (invert ? colorWhite : colorBrandBase)};
      }
    `};
`;

const defaultVariationStyles = css<TabWrapperProps>`
  ${breakpoint(STACKED_BREAKPOINT)`
    ${px(6)};

    &:hover {
      box-shadow: 0 ${BORDER_WIDTH} ${({invert}: {invert?: boolean}) => (invert ? WHITE_20 : BLACK_20)};
    }
  `};

  ${({selected}) =>
    selected &&
    css`
      ${breakpoint(STACKED_BREAKPOINT)`
      box-shadow: 0 ${BORDER_WIDTH} ${({invert}: {invert?: boolean}) => (invert ? colorWhite : colorBrandBase)};
      &:hover {
        box-shadow: 0 ${BORDER_WIDTH} ${({invert}: {invert?: boolean}) => (invert ? colorWhite : colorBrandBase)};
      }
    `};
    `};
`;

const containerVariationStyles = css<TabWrapperProps>`
  ${breakpoint(STACKED_BREAKPOINT)`
    color: ${colorWhite};
    background-color: ${colorBrandBase};

    ${px(6)};
    
    &:not(:first-child) {
      ${ml(2)};
    }
  `};

  ${({selected}) =>
    selected
      ? css`
          ${breakpoint(STACKED_BREAKPOINT)`
            color: ${copyColor};
            background-color: ${colorWhite};
            box-shadow: none;

            &:hover {
              box-shadow: none;
            }
          `};
        `
      : css`
          ${breakpoint(STACKED_BREAKPOINT)`
            &:hover {
              box-shadow: none;
              background-color: ${colorBrandDark};
            }
          `}
        `};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TabWrapper = styled(({fill, invert, variation, ...rest}) => <ReactTab {...rest} />)<TabWrapperProps>`
  display: block;
  position: relative;
  ${p(4)};
  ${baseStyles};

  ${({variation}) =>
    variation === 'container'
      ? css`
          ${containerVariationStyles};
        `
      : css`
          ${defaultVariationStyles};
        `};
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const Title = styled(({selected, subtitle, variation, ...rest}) => <div {...rest} />)<TitleProps>`
  font-size: ${copyFontSize};

  ${breakpoint(STACKED_BREAKPOINT)`
    text-align: center;
  `};

  ${({subtitle, selected, variation}) =>
    variation === 'container'
      ? `
      font-weight: 700
    `
      : subtitle
      ? `font-weight: 700`
      : selected && `font-weight: 700`};
`;

export const Subtitle = styled.div`
  font-size: ${copyFontSizeSmall};

  ${breakpoint(STACKED_BREAKPOINT)`
    text-align: center;
  `};
`;

const Tab: React.FC<TabProps> & {tabsRole: string} = ({selected, subtitle, variation, children, ...otherProps}) => (
  <TabWrapper selected={selected} variation={variation} {...otherProps}>
    <Title selected={selected} subtitle={subtitle} variation={variation}>
      {children}
    </Title>
    {subtitle && <Subtitle>{subtitle}</Subtitle>}
  </TabWrapper>
);

Tab.propTypes = {
  selected: PropTypes.bool,
  subtitle: PropTypes.string,
  variation: PropTypes.string,
  children: PropTypes.node
};

Tab.tabsRole = 'Tab';
Tab.displayName = 'Tab';

export default Tab;
